// AiSidebar.tsx - Updated with SearchableDropdown and smaller buttons

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Button,
  IconButton,
  TextField,
  Collapse,
  Chip,
  FormControl,
  InputLabel,
  FormHelperText,
  Paper,
  Stack,
} from '@mui/material';
import {
  Add,
  Chat as ChatIcon,
  Create as CreateIcon,
  Update as UpdateIcon,
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import CampaignAssignedDropdown from '../Shared/CampaignAssignmentDropdown/index.tsx';
import SearchableDropdown from '../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import {
  CATEGORIES_WITH_SUBCATEGORIES,
  getCategoryByValue,
  getSubCategories,
  getCategoryColor,
  getCategoryIcon,
  CategoryData,
} from './categoryData.ts';

// ── Interfaces ──────────────────────────────────────────────────────────────

interface ApestmicAiSidebarProps {
  isMobile: boolean;
  onClose?: () => void;
  navigate?: (path: string) => void;
  activeTab?: number;
  onTabChange?: (i: number) => void;
  companyName?: string;
  onNewConversation?: () => void;
  conversationList?: any[];
  onSelectConversation?: (conversationId: string) => void;
  selectedConversationId?: string;
  projectList?: Project[];
  selectedProjectId?: string | null;
  onSelectProject?: (projectId: string | null) => void;
  onCreateProject?: (name: string, description?: string, metadata?: any) => void;
  onUpdateProject?: (projectId: string, name: string, description?: string, metadata?: any) => void;
  onLeadFormFetched?: (leadFormId: number) => void;
  onCampaignSelected?: (campaign: any) => void;
  onResetChips?: () => void;
  isCampaignSelected?: () => void;
}

interface Project {
  id: string;
  name: string;
  description?: string;
  emoji?: string;
  chatCount?: number;
  chats?: any[];
  sourceFiles?: any[];
  category?: string;
  subCategory?: string;
  projectType?: 'create' | 'update';
}

// ── ProjectRow Component ────────────────────────────────────────────────────

interface ProjectRowProps {
  project: Project;
  expandedProjects: Record<string, boolean>;
  toggleProject: (id: string) => void;
  selectedProjectId?: string | null;
  selectedConversationId?: string;
  onSelectProject?: (id: string | null) => void;
  onNewConversation?: () => void;
  onSelectConversation?: (id: string) => void;
  categoryColor?: string;
  categoryIcon?: string;
}

const ProjectRow: React.FC<ProjectRowProps> = ({
  project,
  expandedProjects,
  toggleProject,
  selectedProjectId,
  selectedConversationId,
  onSelectProject,
  onNewConversation,
  onSelectConversation,
  categoryColor = '#7C3AED',
  categoryIcon = '📁',
}) => {
  const isExpanded = !!expandedProjects[project.id];
  const projectChats = project.chats || [];

  return (
    <Box sx={{ mb: 0.3 }}>
      <ListItemButton
        onClick={() => {
          toggleProject(project.id);
          onSelectProject?.(project.id);
        }}
        sx={{
          borderRadius: '8px',
          py: 0.7,
          px: 1,
          bgcolor: selectedProjectId === project.id ? `${categoryColor}14` : 'transparent',
          borderLeft: selectedProjectId === project.id ? `3px solid ${categoryColor}` : '3px solid transparent',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
        }}
      >
        <ListItemIcon sx={{ minWidth: 30 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '6px',
              bgcolor: selectedProjectId === project.id ? `${categoryColor}20` : 'rgba(0,0,0,0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
            }}
          >
            {categoryIcon || '📁'}
          </Box>
        </ListItemIcon>

        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: selectedProjectId === project.id ? 600 : 400,
              color: selectedProjectId === project.id ? categoryColor : '#111',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
            }}
          >
            {project.name}
          </Typography>
          {project.subCategory && (
            <Chip
              label={project.subCategory}
              size="small"
              sx={{
                fontSize: 9,
                height: 16,
                bgcolor: `${categoryColor}12`,
                color: categoryColor,
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
          )}
          {project.projectType && (
            <Chip
              label={project.projectType === 'create' ? 'Create' : 'Update'}
              size="small"
              sx={{
                fontSize: 8,
                height: 14,
                bgcolor: project.projectType === 'create' ? '#10B98115' : '#F59E0B15',
                color: project.projectType === 'create' ? '#10B981' : '#F59E0B',
                '& .MuiChip-label': { px: 0.6 },
              }}
            />
          )}
          {project.chatCount !== undefined && (
            <Typography sx={{ fontSize: 10, color: '#aaa', flexShrink: 0 }}>
              {project.chatCount} chat{project.chatCount !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onSelectProject?.(project.id);
            onNewConversation?.();
          }}
          sx={{
            p: 0.3,
            ml: 0.5,
            color: '#bbb',
            '&:hover': { color: '#000', bgcolor: 'rgba(0,0,0,0.06)' },
          }}
        >
          <Add sx={{ fontSize: 13 }} />
        </IconButton>

        {isExpanded ? (
          <ExpandLessIcon sx={{ fontSize: 14, color: '#bbb' }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 14, color: '#bbb' }} />
        )}
      </ListItemButton>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <List dense disablePadding sx={{ pl: 2, mt: 0.2 }}>
          {projectChats.length === 0 ? (
            <ListItem sx={{ py: 0.4, px: 1 }}>
              <Typography sx={{ fontSize: 11, color: '#ccc', fontStyle: 'italic' }}>
                No chats yet
              </Typography>
            </ListItem>
          ) : (
            projectChats.map((chat: any, idx: number) => (
              <ListItem key={chat.conversation_id || idx} disablePadding sx={{ mb: 0.2 }}>
                <ListItemButton
                  selected={chat.conversation_id === selectedConversationId}
                  onClick={() => {
                    onSelectProject?.(project.id);
                    onSelectConversation?.(chat.conversation_id);
                  }}
                  sx={{
                    borderRadius: '7px',
                    py: 0.5,
                    px: 1,
                    '&.Mui-selected': { bgcolor: 'rgba(0,0,0,0.06)' },
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <ChatIcon sx={{ fontSize: 11, color: '#bbb' }} />
                  </ListItemIcon>
                  <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', flex: 1, gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: '#222',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                      }}
                    >
                      {chat.query || 'Conversation'}
                    </Typography>
                    {chat.created_at && (
                      <Typography sx={{ fontSize: 10, color: '#bbb', flexShrink: 0 }}>
                        {new Date(chat.created_at).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
      </Collapse>
    </Box>
  );
};

// ── CategoryGroup Component ────────────────────────────────────────────────

interface CategoryGroupProps {
  category: CategoryData;
  projects: Project[];
  expandedProjects: Record<string, boolean>;
  toggleProject: (id: string) => void;
  selectedProjectId?: string | null;
  selectedConversationId?: string;
  onSelectProject?: (id: string | null) => void;
  onNewConversation?: () => void;
  onSelectConversation?: (id: string) => void;
  categoryExpanded: boolean;
  onToggleCategory: () => void;
}

const CategoryGroup: React.FC<CategoryGroupProps> = ({
  category,
  projects,
  expandedProjects,
  toggleProject,
  selectedProjectId,
  selectedConversationId,
  onSelectProject,
  onNewConversation,
  onSelectConversation,
  categoryExpanded,
  onToggleCategory,
}) => {
  const color = getCategoryColor(category.value);
  const icon = category.icon;

  if (projects.length === 0) return null;

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={onToggleCategory}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          cursor: 'pointer',
          borderRadius: '6px',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
        }}
      >
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '4px',
            bgcolor: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#333', flex: 1 }}>
          {category.label}
        </Typography>
        <Typography sx={{ fontSize: 10, color: '#aaa' }}>
          {projects.length}
        </Typography>
        {categoryExpanded ? (
          <ExpandLessIcon sx={{ fontSize: 14, color: '#bbb' }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 14, color: '#bbb' }} />
        )}
      </Box>

      <Collapse in={categoryExpanded} timeout="auto" unmountOnExit>
        <List dense disablePadding sx={{ pl: 2 }}>
          {projects.map((project) => (
            <ProjectRow
              key={project.id}
              project={project}
              expandedProjects={expandedProjects}
              toggleProject={toggleProject}
              selectedProjectId={selectedProjectId}
              selectedConversationId={selectedConversationId}
              onSelectProject={onSelectProject}
              onNewConversation={onNewConversation}
              onSelectConversation={onSelectConversation}
              categoryColor={color}
              categoryIcon={icon}
            />
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

// ── Main AiSidebar Component ──────────────────────────────────────────────

const AiSidebar: React.FC<ApestmicAiSidebarProps> = ({
  isMobile,
  onClose,
  activeTab,
  onTabChange,
  companyName,
  onNewConversation,
  isCampaignSelected,
  conversationList = [],
  onSelectConversation,
  selectedConversationId,
  projectList = [],
  selectedProjectId,
  onSelectProject,
  onCreateProject,
  onUpdateProject,
  onLeadFormFetched,
  onCampaignSelected,
  onResetChips,
}) => {
  const navigate = useNavigate();
  const [expandedProjects, setExpandedProjects] = useState<Record<string, boolean>>({});
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [selectedCampaign, setSelectedCampaign] = useState<any>({});
  const userInfo = localStorage?.getItem('userInfo');
  const CampanyID = userInfo ? JSON.parse(userInfo)?.profile?.organisation?.organisation_id : null;
  const ORGANIZATION_ID = CampanyID;

  // State for inline project creation/update
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CategoryData | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [projectType, setProjectType] = useState<'create' | 'update'>('create');
  const [selectedProjectForUpdate, setSelectedProjectForUpdate] = useState<Project | null>(null);

  // Get available subcategories based on selected category
  const availableSubCategories = selectedCategory ? getSubCategories(selectedCategory.value) : [];

  // Get projects filtered by category and subcategory for update mode
  const filteredProjectsForUpdate = useMemo(() => {
    if (projectType === 'update' && selectedCategory && selectedSubCategory) {
      return projectList.filter(
        (project) =>
          project.category === selectedCategory.value &&
          project.subCategory === selectedSubCategory
      );
    }
    return [];
  }, [projectType, selectedCategory, selectedSubCategory, projectList]);

  // Handle project type change
  useEffect(() => {
    if (projectType === 'update') {
      setSelectedProjectForUpdate(null);
    } else {
      setSelectedProjectForUpdate(null);
    }
  }, [projectType]);

  const toggleProject = (projectId: string) => {
    setExpandedProjects((prev) => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const toggleCategory = (categoryValue: string) => {
    setExpandedCategories((prev) => ({ ...prev, [categoryValue]: !prev[categoryValue] }));
  };

  const handleCreateProject = () => {
    if (projectType === 'create') {
      if (selectedCategory && selectedSubCategory) {
        let projectName = newProjectName.trim();
        if (!projectName) {
          const actionPrefix = projectType === 'create' ? 'Create' : 'Update';
          projectName = `${actionPrefix} ${selectedSubCategory}`;
        }

        onCreateProject?.(projectName, newProjectDescription.trim(), {
          category: selectedCategory.value,
          subCategory: selectedSubCategory,
          projectType: projectType,
        });

        resetCreateForm();
      }
    } else if (projectType === 'update') {
      if (selectedProjectForUpdate) {
        let projectName = newProjectName.trim() || selectedProjectForUpdate.name;
        onUpdateProject?.(
          selectedProjectForUpdate.id,
          projectName,
          newProjectDescription.trim() || selectedProjectForUpdate.description || '',
          {
            category: selectedCategory?.value,
            subCategory: selectedSubCategory,
            projectType: 'update',
          }
        );
        resetCreateForm();
      }
    }
  };

  const resetCreateForm = () => {
    setNewProjectName('');
    setNewProjectDescription('');
    setSelectedCategory(null);
    setSelectedSubCategory('');
    setProjectType('create');
    setSelectedProjectForUpdate(null);
    setShowCreateForm(false);
  };

  // Group projects by category
  const groupedProjects = useMemo(() => {
    const groups: Record<string, Project[]> = {};

    (projectList || []).forEach((project) => {
      const category = project.category || 'others';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(project);
    });

    return groups;
  }, [projectList]);

  // Check if a category has projects
  const hasProjectsInCategory = (categoryValue: string) => {
    return groupedProjects[categoryValue]?.length > 0;
  };

  // Get projects for a category with chats
  const getProjectsForCategory = (categoryValue: string) => {
    const projects = groupedProjects[categoryValue] || [];
    return projects.map((project) => {
      const projectChats = conversationList.filter(
        (conv) => conv.project_id === project.id
      );
      return {
        ...project,
        chats: projectChats,
        chatCount: projectChats.length,
      };
    });
  };

  // Get all chats that are not associated with any project
  const unassignedChats = useMemo(() => {
    const projectChatIds = new Set();
    (projectList || []).forEach((project) => {
      (project.chats || []).forEach((chat: any) => {
        if (chat.conversation_id) {
          projectChatIds.add(chat.conversation_id);
        }
      });
    });
    return conversationList.filter(
      (conv) => !projectChatIds.has(conv.conversation_id) && !conv.project_id
    );
  }, [conversationList, projectList]);

  // Category options for dropdown
  const categoryOptions = CATEGORIES_WITH_SUBCATEGORIES.map((cat) => ({
    ...cat,
    label: `${cat.icon} ${cat.label}`,
    value: cat.value,
  }));

  // Subcategory options for dropdown
  const subCategoryOptions = availableSubCategories.map((sub) => ({
    label: sub,
    value: sub,
  }));

  // Project options for update
  const projectOptionsForUpdate = filteredProjectsForUpdate.map((project) => ({
    ...project,
    label: project.name,
    value: project.id,
  }));

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        borderRadius: isMobile ? 0 : '12px',
        overflow: 'hidden',
        width: '100%',
        position: 'relative',
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>
            Machadalo AI Suite
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>
            Workspace
          </Typography>
        </Box>

        <Button
          size="small"
          onClick={() => navigate('/r/machadalostudio/history')}
          sx={{
            minWidth: 'auto',
            px: 1.2,
            py: 0.5,
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: 11,
            bgcolor: 'rgba(255,255,255,0.08)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.15)',
            },
          }}
        >
          History
        </Button>

        {isMobile && onClose && (
          <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* ── Campaign Dropdown ── */}
      <Box sx={{ px: 1.5, mt: 1, flexShrink: 0 }}>
        <CampaignAssignedDropdown
          value={selectedCampaign}
          onChange={(val) => {
            setSelectedCampaign(val);
            onCampaignSelected?.(val);
          }}
          organisationId={ORGANIZATION_ID}
          autoSelectValue={true}
        />
      </Box>

      {/* ── New Chat + Add Project Buttons ── */}
      <Box sx={{ px: 1.5, pt: 1, flexShrink: 0 }}>
        <Button
          fullWidth
          // onClick={onNewConversation}
          onClick={() => {
            if (!isCampaignSelected) return;
            onNewConversation?.();
          }}
          startIcon={<Add sx={{ fontSize: 15 }} />}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 400,
            color: isCampaignSelected ? '#111' : '#bbb',
            px: 1.5,
            py: 0.9,
            borderRadius: '8px',
            bgcolor: 'transparent',
            boxShadow: 'none',
            '&:hover': { bgcolor: isCampaignSelected ? 'rgba(0,0,0,0.04)' : 'transparent' },
            cursor: isCampaignSelected ? 'pointer' : 'not-allowed',
          }}
        >
          New chat
        </Button>
        <Button
          fullWidth
          onClick={() => {
            if (!isCampaignSelected) return;
            onResetChips?.();
            setShowCreateForm(!showCreateForm);
            if (showCreateForm) {
              resetCreateForm();
            }
          }}
          startIcon={showCreateForm ? <CloseIcon sx={{ fontSize: 13 }} /> : <Add sx={{ fontSize: 13 }} />}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 400,
            color: !isCampaignSelected ? '#bbb' : showCreateForm ? '#EF4444' : '#111',
            px: 1.5,
            py: 0.9,
            borderRadius: '8px',
            bgcolor: showCreateForm ? '#FEF2F2' : 'transparent',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: !isCampaignSelected
                ? 'transparent'
                : showCreateForm
                  ? '#FEE2E2'
                  : 'rgba(0,0,0,0.04)',
            },
            cursor: isCampaignSelected ? 'pointer' : 'not-allowed',
          }}
        >
          {showCreateForm ? 'Cancel' : 'Add project'}
        </Button>
      </Box>

      {/* ── Inline Create/Update Project Form ── */}
      <Collapse in={showCreateForm} timeout="auto" unmountOnExit>
        <Box sx={{ px: 1.5, py: 1, flexShrink: 0 }}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: '#F8FAFC',
              border: '1px solid #E2E8F0',
              borderRadius: '10px',
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 1.5 }}>
              {projectType === 'create' ? 'Create New Project' : 'Update Existing Project'}
            </Typography>

            <Stack spacing={1.5}>
              {/* Project Type Toggle - Smaller Buttons */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  size="small"
                  variant={projectType === 'create' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setProjectType('create');
                    setSelectedProjectForUpdate(null);
                    setNewProjectName('');
                    setNewProjectDescription('');
                  }}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontSize: 10,
                    borderRadius: '6px',
                    py: 0.4,
                    minHeight: '28px',
                    ...(projectType === 'create' ? { boxShadow: 'none' } : {}),
                  }}
                >
                  <CreateIcon sx={{ fontSize: 11, mr: 0.3 }} />
                  Create
                </Button>
                <Button
                  size="small"
                  variant={projectType === 'update' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setProjectType('update');
                    setSelectedProjectForUpdate(null);
                    setNewProjectName('');
                    setNewProjectDescription('');
                  }}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontSize: 10,
                    borderRadius: '6px',
                    py: 0.4,
                    minHeight: '28px',
                    ...(projectType === 'update' ? { boxShadow: 'none' } : {}),
                  }}
                >
                  <UpdateIcon sx={{ fontSize: 11, mr: 0.3 }} />
                  Update
                </Button>
              </Box>

              {/* Category Dropdown using SearchableDropdown */}
              <SearchableDropdown
                label="Category *"
                options={categoryOptions}
                value={selectedCategory}
                onChange={(val) => {
                  setSelectedCategory(val as CategoryData | null);
                  setSelectedSubCategory('');
                  setSelectedProjectForUpdate(null);
                  setNewProjectName('');
                  setNewProjectDescription('');
                }}
                labelKey="label"
                valueKey="value"
                size="small"
                sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
              />

              {/* SubCategory Dropdown using SearchableDropdown */}
              <SearchableDropdown
                label="Subcategory *"
                options={subCategoryOptions}
                value={selectedSubCategory ? { label: selectedSubCategory, value: selectedSubCategory } : null}
                onChange={(val) => {
                  const subValue = val ? (val as any).value : '';
                  setSelectedSubCategory(subValue);
                  setSelectedProjectForUpdate(null);
                  if (projectType === 'update' && selectedCategory) {
                    const projects = projectList.filter(
                      (p) => p.category === selectedCategory.value && p.subCategory === subValue
                    );
                    if (projects.length > 0) {
                      setSelectedProjectForUpdate(projects[0]);
                      setNewProjectName(projects[0].name);
                      setNewProjectDescription(projects[0].description || '');
                    }
                  }
                }}
                labelKey="label"
                valueKey="value"
                size="small"
                disabled={!selectedCategory}
                sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
              />

              {/* For Update Mode: Show existing projects */}
              {projectType === 'update' && selectedCategory && selectedSubCategory && (
                <SearchableDropdown
                  label="Select Project to Update *"
                  options={projectOptionsForUpdate}
                  value={selectedProjectForUpdate ? { label: selectedProjectForUpdate.name, value: selectedProjectForUpdate.id } : null}
                  onChange={(val) => {
                    const project = val ? filteredProjectsForUpdate.find(p => p.id === (val as any).value) : null;
                    if (project) {
                      setSelectedProjectForUpdate(project);
                      setNewProjectName(project.name);
                      setNewProjectDescription(project.description || '');
                    }
                  }}
                  labelKey="label"
                  valueKey="value"
                  size="small"
                  disabled={projectOptionsForUpdate.length === 0}
                  sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
                />
              )}

              {/* Project Name */}
              <TextField
                fullWidth
                size="small"
                label={projectType === 'create' ? "Project Name (Optional)" : "Project Name *"}
                placeholder={projectType === 'create'
                  ? `Create ${selectedSubCategory || 'Project'}`
                  : "Enter project name"
                }
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                sx={{
                  '& .MuiInputBase-input': { fontSize: 12 },
                  '& .MuiInputLabel-root': { fontSize: 12 },
                  bgcolor: '#fff',
                }}
                required={projectType === 'update'}
              />

              {/* Description */}
              <TextField
                fullWidth
                size="small"
                label="Description (Optional)"
                placeholder="Describe this project..."
                value={newProjectDescription}
                onChange={(e) => setNewProjectDescription(e.target.value)}
                multiline
                rows={2}
                sx={{
                  '& .MuiInputBase-input': { fontSize: 12 },
                  '& .MuiInputLabel-root': { fontSize: 12 },
                  bgcolor: '#fff',
                }}
              />

              {/* Create/Update Button - Smaller */}
              <Button
                fullWidth
                variant="contained"
                disabled={
                  !selectedCategory ||
                  !selectedSubCategory ||
                  (projectType === 'update' && !selectedProjectForUpdate) ||
                  (projectType === 'update' && !newProjectName.trim())
                }
                onClick={handleCreateProject}
                sx={{
                  textTransform: 'none',
                  fontSize: 11,
                  borderRadius: '8px',
                  py: 0.6,
                  minHeight: '32px',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                {projectType === 'create' ? 'Create' : 'Update'} Project
              </Button>
            </Stack>
          </Paper>
        </Box>
      </Collapse>

      <Box sx={{ mx: 2, borderTop: '1px solid #f0f0f0', flexShrink: 0 }} />

      {/* ── Projects and Chats Container ── */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          px: 1.5,
          pt: 1,
        }}
      >
        {/* Projects Section */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Typography
            sx={{
              color: '#000000',
              fontSize: 9.5,
              fontWeight: 800,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              px: 1,
              mb: 0.8,
              flexShrink: 0,
            }}
          >
            Projects
          </Typography>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              pr: 0.3,
              minHeight: 0,
              '&::-webkit-scrollbar': { width: 3 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#e0e0e0', borderRadius: 4 },
              '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
            }}
          >
            {Object.keys(groupedProjects).length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <FolderIcon sx={{ fontSize: 40, color: '#e0e0e0', mb: 1 }} />
                <Typography sx={{ color: '#ccc', fontSize: 12, fontStyle: 'italic' }}>
                  No projects yet
                </Typography>
                <Typography sx={{ color: '#ddd', fontSize: 11, mt: 0.5 }}>
                  Click "Add project" above to create one
                </Typography>
              </Box>
            ) : (
              CATEGORIES_WITH_SUBCATEGORIES.map((category) => {
                if (!hasProjectsInCategory(category.value)) return null;

                const projects = getProjectsForCategory(category.value);
                const isCategoryExpanded = expandedCategories[category.value] !== false;

                return (
                  <CategoryGroup
                    key={category.value}
                    category={category}
                    projects={projects}
                    expandedProjects={expandedProjects}
                    toggleProject={toggleProject}
                    selectedProjectId={selectedProjectId}
                    selectedConversationId={selectedConversationId}
                    onSelectProject={onSelectProject}
                    onNewConversation={onNewConversation}
                    onSelectConversation={onSelectConversation}
                    categoryExpanded={isCategoryExpanded}
                    onToggleCategory={() => toggleCategory(category.value)}
                  />
                );
              })
            )}
          </Box>
        </Box>

        {/* Chats Section - Only show if there are chats */}
        {conversationList.length > 0 && (
          <Box
            sx={{
              flexShrink: 0,
              maxHeight: '30%',
              minHeight: '60px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderTop: '1px solid #f0f0f0',
              pt: 1,
              mt: 1,
            }}
          >
            <Typography
              sx={{
                color: '#000000',
                fontSize: 9.5,
                fontWeight: 800,
                letterSpacing: 1.4,
                textTransform: 'uppercase',
                px: 1,
                mb: 0.8,
                flexShrink: 0,
              }}
            >
              Chats
            </Typography>

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                pr: 0.3,
                minHeight: 0,
                '&::-webkit-scrollbar': { width: 3 },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#e0e0e0', borderRadius: 4 },
                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
              }}
            >
              {unassignedChats.length === 0 ? (
                <Typography sx={{ color: '#ccc', fontSize: 12, fontStyle: 'italic', px: 1 }}>
                  All chats are assigned to projects
                </Typography>
              ) : (
                <List dense disablePadding>
                  {unassignedChats.map((chat: any, idx: number) => (
                    <ListItem key={chat.conversation_id || idx} disablePadding sx={{ mb: 0.2 }}>
                      <ListItemButton
                        selected={chat.conversation_id === selectedConversationId}
                        onClick={() => onSelectConversation?.(chat.conversation_id)}
                        sx={{
                          borderRadius: '8px',
                          py: 0.5,
                          px: 1,
                          '&.Mui-selected': { bgcolor: 'rgba(0,0,0,0.05)' },
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <ChatIcon sx={{ fontSize: 11, color: '#bbb' }} />
                        </ListItemIcon>
                        <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', flex: 1, gap: 1 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 400,
                              color: '#111',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              flex: 1,
                            }}
                          >
                            {chat.query || 'Conversation'}
                          </Typography>
                          {chat.created_at && (
                            <Typography sx={{ fontSize: 10, color: '#bbb', flexShrink: 0 }}>
                              {new Date(chat.created_at).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AiSidebar;